import { useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DeviceEventEmitter } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import EmptyStateArchivedProjects from '@components/EmptyState/EmptyStateArchivedProject';
import { ContactType } from '@components/Invite/Contact';
import { InviteRequestButton } from '@components/Invite/InviteRequestButton';
import { Box } from '@components/Restyle';
import { FilterToolbarSkeleton } from '@components/shared/skeletons';
import {
  MyTaskList,
  TaskSectionNew,
} from '@components/TaskList/MyTaskList.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  SortOptionValue,
  sortValueToTaskSortOption,
} from '@components/Web/PopupTaskSortButton.web';
import { ProjectTasksFilterPanel } from '@components/Web/ProjectTasksFilterPanel.web';
import {
  Task,
  TaskGroup,
  TaskMemberRole,
  TaskPriority,
  TaskSortOption,
  TeamMemberRole,
  User,
} from '@graphql/generated';
import { useListTasksFromQuery } from '@hooks/useListTasksFromQuery';
import useMe from '@hooks/useMe';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { useProjectMembership } from '@hooks/useProjectMembership';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { beginningOfDayUTC } from '@utils/formatters/date';
import {
  NonMemberAssignee,
  reconcileNonMemberAssignees,
} from '@utils/projects/nonMemberAssignees';
import { INVITE_SIGNUP_TASKS_WAITING_KEY } from '@utils/startConversationTutorial';
import { AsyncStorage } from '@utils/storage';
import {
  filterTasks,
  groupTasksByDueGroup,
  isTaskAssignedTo,
  localTodayKey,
} from '@utils/tasks/projectTaskGroups';

import type { ProjectDetailTabsScreenProps } from '@navigation/projects/project-detail-tabs.web';

// Page size for the single project task list fetch. Large enough to cover
// typical projects in one round-trip; hasNextPage/loadMore paginate beyond it.
const PROJECT_TASKS_FETCH_SIZE = 200;

export const ProjectTasks: React.FC = () => {
  const route = useRoute<ProjectDetailTabsScreenProps<'tasks'>['route']>();
  const projectUUId = route.params?.projectUUId;

  const [projectId, setProjectId] = useState<string>();

  const { project } = useProjectFromQuery({
    projectUUId,
  });

  useEffect(() => {
    // Guard on project?.id to prevent unnecessary state updates from object reference changes
    if (project?.id && project.id !== projectId) {
      setProjectId(project.id);
    }
  }, [project?.id, projectId]);

  const { me } = useMe();
  const { isMemberOrOwner, isEditorOrHigher, isAdminOrHigher } =
    useProjectMembership(project);

  const isTeamOwnerOrAdmin =
    project?.currentUserTeamRole === 'OWNER' ||
    project?.currentUserTeamRole === 'ADMIN';
  const isTeamMember = project?.currentUserTeamRole === TeamMemberRole.Member;

  const nonMember = !(isMemberOrOwner || isTeamOwnerOrAdmin || isTeamMember);

  // Show the onboarding "no tasks" banner for project members below editor level
  const isProjectMember = isMemberOrOwner && !isEditorOrHigher;
  // "You have tasks waiting" is part of invite-signup Member onboarding only
  // (TA-10892 #26). Invitees join as Editor by default (defaultProjectRole),
  // so it only skips Owner/Admin — the people assigning work.
  const [isInviteSignup] = useState(
    () =>
      globalThis.localStorage?.getItem?.(INVITE_SIGNUP_TASKS_WAITING_KEY) ===
      '1'
  );
  const canSeeTaskTooltip =
    isInviteSignup && isMemberOrOwner && !isAdminOrHigher;

  const [bannerDismissed, setBannerDismissed] = useState(true);
  const [tooltipShown, setTooltipShown] = useState(true);

  useEffect(() => {
    // Load for either onboarding surface: the banner still covers every
    // project member below editor level, invitee or not.
    if (!me?.id || !projectId || !(isProjectMember || canSeeTaskTooltip)) {
      return;
    }
    const bannerKey = `no_tasks_banner_dismissed_${me.id}_${projectId}`;
    const tooltipKey = `task_tooltip_shown_${me.id}_${projectId}`;

    // Users who signed up via a task invitation should not see the "You have
    // tasks waiting" tooltip — pre-dismiss it and persist so it stays hidden.
    // Banner state is still loaded from AsyncStorage so invited users who
    // genuinely have no tasks yet still see the "no tasks" banner.
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage?.getItem('skip_task_tooltip') === '1'
    ) {
      window.sessionStorage.removeItem('skip_task_tooltip');
      void AsyncStorage.setItem(tooltipKey, '1');
      setTooltipShown(true);
      AsyncStorage.getItem(bannerKey).then((bannerVal) => {
        setBannerDismissed(!!bannerVal);
      });
      return;
    }

    Promise.all([
      AsyncStorage.getItem(bannerKey),
      AsyncStorage.getItem(tooltipKey),
    ]).then(([bannerVal, tooltipVal]) => {
      setBannerDismissed(!!bannerVal);
      setTooltipShown(!!tooltipVal);
    });
  }, [me?.id, projectId, isProjectMember, canSeeTaskTooltip]);

  const handleBannerDismiss = useCallback(async () => {
    if (!me?.id || !projectId) return;
    setBannerDismissed(true);
    await AsyncStorage.setItem(
      `no_tasks_banner_dismissed_${me.id}_${projectId}`,
      '1'
    );
  }, [me?.id, projectId]);

  const handleTooltipDismiss = useCallback(async () => {
    if (!me?.id || !projectId) return;
    setTooltipShown(true);
    // First task opened — invite-signup onboarding for this tooltip is done.
    globalThis.localStorage?.removeItem?.(INVITE_SIGNUP_TASKS_WAITING_KEY);
    await AsyncStorage.setItem(`task_tooltip_shown_${me.id}_${projectId}`, '1');
  }, [me?.id, projectId]);

  const [isMaximize] = useState(false);

  const [selectedAssignees, setSelectedAssignees] = useState<ContactType[]>([]);
  const [selectedDueDateRange, setSelectedDueDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  });
  const [selectedPriorities, setSelectedPriorities] = useState<TaskPriority[]>(
    []
  );
  const [selectedSort, setSelectedSort] = useState<SortOptionValue>(undefined);

  const sortOption =
    sortValueToTaskSortOption(selectedSort) ?? TaskSortOption.Rank;

  // Single source of truth for every section on this screen (TA-10112).
  // Sorting stays server-side (Rank has no client-visible field); the
  // Current/Overdue/Completed sections, the "my tasks" check, and the
  // priority/date/assignee filters are all derived from this one cached
  // list, so filter changes trigger no network requests.
  const {
    tasks: allProjectTasks,
    loading: loadingAllProjectTasks,
    pageInfo,
    fetchFromCursorAsync,
  } = useListTasksFromQuery({
    first: PROJECT_TASKS_FETCH_SIZE,
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
    projectIds: projectId ? [projectId] : undefined,
    sortBy: sortOption,
  });

  // Whether the tasks query is currently in flight with nothing to show yet
  // (`!projectId` covers the commit where the project has resolved but the id
  // state hasn't been set, so the query hasn't fired and Apollo `loading` is
  // still false). Drives each section's `loading` (and the filter panel's
  // assignee-list spinner) so a sort-driven refetch — a cache miss for that
  // variable combo, which briefly resets loading+empty just like a first
  // load — still shows a loading skeleton instead of MyTaskList falling
  // through to "No current/overdue/completed tasks" (TA-10273: never show an
  // empty state while a query is in flight).
  const isTasksQueryLoading =
    (!projectId || loadingAllProjectTasks) && allProjectTasks.length === 0;

  // Toolbar-only gate: same condition as isTasksQueryLoading, but latched via
  // hasLoadedProjectTasksOnceRef once the query has resolved for a real
  // (non-skipped) projectId, so that later sort-driven refetch does NOT
  // re-trigger the skeleton and remount ProjectTasksFilterPanel, closing
  // whatever dropdown the user had open (TA-10949 #30). Deliberately NOT
  // reused for section/assignee loading — see isTasksQueryLoading above.
  const hasLoadedProjectTasksOnceRef = useRef(false);
  useEffect(() => {
    if (projectId && !loadingAllProjectTasks) {
      hasLoadedProjectTasksOnceRef.current = true;
    }
  }, [projectId, loadingAllProjectTasks]);
  const isInitialTasksLoad =
    !hasLoadedProjectTasksOnceRef.current && isTasksQueryLoading;

  const filteredTasks = useMemo(
    () =>
      filterTasks(allProjectTasks, {
        priorities:
          selectedPriorities.length > 0 ? selectedPriorities : undefined,
        dueDateBegin: selectedDueDateRange.start
          ? beginningOfDayUTC(
              dayjs(selectedDueDateRange.start).toDate()
            ).toISOString()
          : undefined,
        dueDateEnd: selectedDueDateRange.end
          ? beginningOfDayUTC(
              dayjs(selectedDueDateRange.end).toDate()
            ).toISOString()
          : undefined,
        assigneeIds:
          selectedAssignees.length > 0
            ? selectedAssignees.map((t) => t.id)
            : undefined,
      }),
    [
      allProjectTasks,
      selectedPriorities,
      selectedDueDateRange.start,
      selectedDueDateRange.end,
      selectedAssignees,
    ]
  );

  const { currentTasks, overdueTasks, completedTasks } = useMemo(
    () => groupTasksByDueGroup(filteredTasks, localTodayKey()),
    [filteredTasks]
  );

  const buildSection = useCallback(
    (
      id: TaskGroup | 'COMPLETED',
      name: string,
      data: Task[]
    ): TaskSectionNew => ({
      sectionData: {
        sectionHeader: { id, name, count: data.length },
        data,
        hasNextPage: pageInfo?.hasNextPage,
      },
      loadMore: fetchFromCursorAsync,
      // Sibling of sectionData, not nested inside it — MyTaskList reads
      // d.loading (TaskSectionNew), and its `...d.sectionData` spread drops a
      // nested one (TA-10308). Uses the unlatched flag (not the toolbar's
      // isInitialTasksLoad) so a post-first-load refetch still shows a
      // loading skeleton instead of a false "No tasks" (TA-10949 #30 follow-up).
      loading: isTasksQueryLoading,
    }),
    [pageInfo?.hasNextPage, isTasksQueryLoading, fetchFromCursorAsync]
  );

  const groupedCurrentTasks = buildSection(
    TaskGroup.Current,
    'Current',
    currentTasks
  );
  const groupedPastTasks = buildSection(
    TaskGroup.Overdue,
    'Overdue',
    overdueTasks
  );
  // 'COMPLETED' is not a TaskGroup value, but it is the section id the list
  // components key on (see mapTaskGroupToTaskSection in MyTaskList.web).
  const groupedCompletedTasks = buildSection(
    'COMPLETED',
    'Completed',
    completedTasks
  );

  const myId = me?.id;
  const hasMyTasks = useMemo(
    () =>
      !!myId && allProjectTasks.some((task) => isTaskAssignedTo(task, myId)),
    [allProjectTasks, myId]
  );
  // MyTaskList anchors the tooltip only to an assigned task in the rendered
  // Current/Overdue sections, so completed or filtered-out assignments must
  // not count — otherwise a press could consume an onboarding never shown.
  const hasMyOpenTaskInView = useMemo(
    () =>
      !!myId &&
      [...currentTasks, ...overdueTasks].some((task) =>
        isTaskAssignedTo(task, myId)
      ),
    [currentTasks, overdueTasks, myId]
  );
  // Single source of truth for whether the tooltip is actually on screen, so a
  // task press only consumes the onboarding when the user has seen it.
  const showFirstTaskTooltip =
    canSeeTaskTooltip && hasMyOpenTaskInView && !tooltipShown;

  const initialAssigneeContacts = useMemo(() => {
    const users: User[] = [];
    for (const task of allProjectTasks) {
      for (const member of task.members ?? []) {
        if (
          member.role === TaskMemberRole.Assignee &&
          member.user &&
          !users.some((u) => u.id === member.user.id)
        ) {
          users.push(member.user);
        }
      }
    }
    return convertUsersToContacts(users);
  }, [allProjectTasks]);

  const { setIsTaskWebPanelOpen, setIdTaskDetailOpen, setMainPanelWidth } =
    useWebDrawer();

  // TA-10892 #32: the Got-crew banner names the assignees who haven't joined.
  // Reconcile the list it reads with the loaded tasks (email invitations +
  // assignees outside the project) and current membership, so it works
  // however the task was assigned and drops people once they join.
  useEffect(() => {
    if (!projectId || !project?.members) return;
    const storedForProject =
      globalThis.localStorage?.getItem?.(
        'non_member_assignee_emails_project_id'
      ) === projectId;
    let stored: NonMemberAssignee[] = [];
    if (storedForProject) {
      try {
        const parsed = JSON.parse(
          globalThis.localStorage?.getItem?.('non_member_assignee_emails') ??
            '[]'
        ) as unknown;
        if (Array.isArray(parsed)) stored = parsed;
      } catch {
        stored = [];
      }
    }
    const next = reconcileNonMemberAssignees({
      stored,
      tasks: allProjectTasks,
      projectMembers: project.members,
      assigneeRole: TaskMemberRole.Assignee,
    });
    if (JSON.stringify(next) === JSON.stringify(stored)) return;
    if (next.length === 0 && !storedForProject) return;

    if (next.length === 0) {
      globalThis.localStorage?.removeItem?.('non_member_assignee_emails');
      globalThis.localStorage?.removeItem?.(
        'non_member_assignee_emails_project_id'
      );
    } else {
      globalThis.localStorage?.setItem?.(
        'non_member_assignee_emails_project_id',
        projectId
      );
      globalThis.localStorage?.setItem?.(
        'non_member_assignee_emails',
        JSON.stringify(next)
      );
    }
    DeviceEventEmitter.emit('NON_MEMBER_ASSIGNEES_UPDATED');
  }, [allProjectTasks, projectId, project?.members]);

  if (!projectUUId) return null;

  if (nonMember)
    return (
      <InviteRequestButton
        currentUserHasPendingMemberAdditionRequest={
          project?.currentUserHasPendingMemberAdditionRequest
        }
        projectId={projectId}
      />
    );

  return (
    <>
      {project?.archivedAt ? (
        <EmptyStateArchivedProjects />
      ) : (
        <Box
          onLayout={(e) =>
            setMainPanelWidth(
              e.nativeEvent.layout.width + theme.spacing.xs + theme.spacing.m
            )
          }
          flex={1}
          flexDirection='row'
          justifyContent='space-between'
          ml='xs'
          mr='m'>
          {!isMaximize && (
            <Box flex={1} mt='xxs'>
              <Box mb='xxs'>
                {isInitialTasksLoad ? (
                  <FilterToolbarSkeleton />
                ) : (
                  <ProjectTasksFilterPanel
                    initialAssignees={initialAssigneeContacts}
                    selectedAssignees={selectedAssignees}
                    setSelectedAssignees={setSelectedAssignees}
                    selectedPriorities={selectedPriorities}
                    setSelectedPriorities={setSelectedPriorities}
                    selectedDueDateRange={selectedDueDateRange}
                    setSelectedDueDateRange={setSelectedDueDateRange}
                    selectedSort={selectedSort}
                    onSelectSort={setSelectedSort}
                    projectMembers={project?.members ?? []}
                    assigneesLoading={isTasksQueryLoading}
                  />
                )}
              </Box>
              <MyTaskList
                key={projectId}
                tasks={[
                  groupedCurrentTasks,
                  groupedPastTasks,
                  groupedCompletedTasks,
                ]}
                onPress={(task) => {
                  if (showFirstTaskTooltip) {
                    handleTooltipDismiss();
                  }
                  setIdTaskDetailOpen(task.id);
                  setIsTaskWebPanelOpen(true);
                }}
                hideProject={true}
                defaultProject={project ?? undefined}
                draggable={sortOption === TaskSortOption.Rank}
                showNoTasksBanner={
                  isProjectMember &&
                  // Unlatched flag (not the toolbar's isInitialTasksLoad) —
                  // hasMyTasks briefly recomputes to false while a sort-driven
                  // refetch has emptied allProjectTasks, so this must reflect
                  // real-time loading or the banner would flash on/off on
                  // every sort change (TA-10949 #30 follow-up).
                  !isTasksQueryLoading &&
                  // hasMyTasks is derived from the fetched page; with more
                  // pages unfetched, "no assigned tasks" is not yet certain.
                  !pageInfo?.hasNextPage &&
                  !hasMyTasks &&
                  !bannerDismissed
                }
                onBannerDismiss={handleBannerDismiss}
                showFirstTaskTooltip={showFirstTaskTooltip}
                onTooltipDismiss={handleTooltipDismiss}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};
